import { type MetaFunction } from '@remix-run/node'
import React from 'react'
import { Loading } from '#app/components/loading'
import { Button } from '#app/components/ui/button.js'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from '#app/components/ui/drawer.tsx'
import { useOptionalUser } from '#app/utils/user'

export const meta: MetaFunction = () => [{ title: 'Otemba Sake' }]

export default function Index() {
	const user = useOptionalUser()

	const products = [
		{
			id: 1,
			name: 'Yuzu Sake',
			href: '#',
			price: '€34',
			description:
				'Een speciale Yuzu Sake (Japanse citrusvrucht) die wordt gemaakt door de Yuzu in de Junmai sake te laten trekken.',
			options: 'Umami',
			imageSrc:
				'https://store.otemba-sake.com/cdn/shop/products/2020-05-1911.20.23_ff11d9da-0d6c-45da-99c8-f385295ac997_300x300.png?v=1601994764',
			imageAlt: 'Yuzu Sake bottle',
		},
		{
			id: 2,
			name: 'Kakurei Junmai Ginjo',
			href: '#',
			price: '€38',
			description:
				'Een lichte, droge en frisse sake met delicate bloemige aromas.',
			options: 'Bloemig',
			imageSrc:
				'https://store.otemba-sake.com/cdn/shop/products/004002_KakureiJG_720.png?v=1628171739',
			imageAlt: 'Kakurei Junmai Ginjo Sake bottle',
		},
		// More products...
	]

	const [isLoading, setIsLoading] = React.useState(true)
	const [isFading, setIsFading] = React.useState(false)

	React.useEffect(() => {
		const timer = setTimeout(() => {
			setIsFading(true)
			setTimeout(() => {
				setIsLoading(false)
			}, 1000)
		}, 3000)

		return () => clearTimeout(timer)
	}, [])

	if (isLoading) {
		return (
			<div
				className={`absolute top-0 z-50 flex h-screen w-screen flex-col items-center justify-center bg-red transition-opacity duration-1000 ${
					isFading ? 'opacity-0' : 'opacity-100'
				}`}
			>
				<Loading />
				<div className="animate-slide-top p-2 font-medium text-4xl text-foreground [animation-delay:0.3s] [animation-fill-mode:backwards]">
					Welcome to Otemba Sake
				</div>
				<div className="animate-slide-top p-2 font-medium text-2xl text-white [animation-delay:0.8s] [animation-fill-mode:backwards]">
					We've been expecting you
				</div>
			</div>
		)
	}

	return (
		<main className="grid h-full place-items-center">
			<div className="relative isolate animate-[fadeInTranslateY_1s_ease-in-out] p-6 lg:px-8">
				<div className="mx-auto max-w-2xl sm:py-48 lg:py-56">
					<div className="hidden sm:mb-8 sm:flex sm:justify-center">
						<div className="relative rounded-full px-3 py-1 text-sm leading-6 ring-1 ring-gray-900/10 hover:ring-gray-900/20">
							New batch coming in two weeks{' '}
							<a href="#" className="font-semibold text-indigo-600">
								<span className="absolute inset-0" aria-hidden="true" />
								Keep me posted <span aria-hidden="true">&rarr;</span>
							</a>
						</div>
					</div>

					<div className="text-center">
						<h1
							data-heading
							className="text-4xl font-bold tracking-tight text-foreground sm:text-6xl"
						>
							Discover your favorite Sake
						</h1>
						<p className="mt-6 text-lg leading-8">
							Worlds finest sake, fresh from the bewery to your door
						</p>
						<div className="mt-10 flex items-center justify-center gap-x-6">
							<Drawer>
								<DrawerTrigger>
									<Button variant={'secondary'}>Gift a Sake</Button>
								</DrawerTrigger>
								<DrawerContent className="__className_212e56 fixed inset-x-4 bottom-4 mx-auto max-w-[361px] overflow-hidden rounded-[36px] bg-[#FEFFFE] outline-none md:mx-auto md:w-full">
									<DrawerHeader>
										<DrawerClose className="flex justify-end">
											<button
												className="focus-visible:shadow-focus-ring-button top right absolute z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[#F7F8F9] text-[#949595] transition-transform focus:scale-95 active:scale-75"
												type="button"
											>
												<svg
													width="12"
													height="12"
													viewBox="0 0 12 12"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
												>
													<path
														d="M10.4854 1.99998L2.00007 10.4853"
														stroke="#999999"
														stroke-width="3"
														stroke-linecap="round"
														stroke-linejoin="round"
													></path>
													<path
														d="M10.4854 10.4844L2.00007 1.99908"
														stroke="#999999"
														stroke-width="3"
														stroke-linecap="round"
														stroke-linejoin="round"
													></path>
												</svg>
											</button>
										</DrawerClose>
										<DrawerTitle>Gift a Sake</DrawerTitle>
									</DrawerHeader>
									<DrawerFooter>
										<Button className="textLeft focus-visible:shadow-focus-ring-button flex h-12 w-full items-center gap-[15px] rounded-[16px] bg-[#F7F8F9] px-4 font-semibold text-[17px] text-[#222222] transition-transform focus:scale-95 active:scale-95 md:font-medium">
											For a friend
										</Button>
										<Button className="textLeft focus-visible:shadow-focus-ring-button flex h-12 w-full items-center gap-[15px] rounded-[16px] bg-[#F7F8F9] px-4 font-semibold text-[17px] text-[#222222] transition-transform focus:scale-95 active:scale-95 md:font-medium">
											For yourself
										</Button>
										<Button className="button-primary">Select a gift</Button>
									</DrawerFooter>
								</DrawerContent>
								<DrawerTrigger></DrawerTrigger>
							</Drawer>
							<a
								href="#"
								className="font-semibold text-sm leading-6 text-gray-900"
							>
								Discover our Sake Course <span aria-hidden="true">→</span>
							</a>
						</div>
					</div>
				</div>
				<div
					className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
					aria-hidden="true"
				>
					<div
						className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
						style={{
							clipPath:
								'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
						}}
					/>
				</div>
			</div>

			<div className="w-full bg-red">
				<div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24 lg:max-w-7xl lg:px-8">
					<div className="text-left">
						<h3
							data-heading
							className="mb-10 pb-4 font-bold tracking-tight text-foreground sm:text-6xl"
						>
							{user ? `Hi ${user.name}` : 'Welcome'}, we've kept your last
							orders ready for you to reorder.
						</h3>
					</div>

					<div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8">
						{products.map((product) => (
							<div
								key={product.id}
								className="group relative flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
							>
								<img
									alt={product.imageAlt}
									src={product.imageSrc}
									className="aspect-[3/4] w-full bg-gray-200 object-cover group-hover:opacity-75 sm:aspect-auto sm:h-96"
								/>
								<div className="flex flex-1 flex-col space-y-2 p-4">
									<h3 className="font-medium text-sm text-gray-900">
										<a href={product.href}>
											<span aria-hidden="true" className="absolute inset-0" />
											{product.name}
										</a>
									</h3>
									<p className="text-sm text-gray-500">{product.description}</p>
									<div className="flex flex-1 flex-col justify-end">
										<p className="text-sm italic text-gray-500">
											{product.options}
										</p>
										<p className="font-medium text-base text-gray-900">
											{product.price}
										</p>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</main>
	)
}
